import sileo from 'sileo';

const CompanyEmployment = new sileo.Model('hqzen', 'company-employments');
const CompanyUser = new sileo.Model('hqzen', 'company-users');
const SidebarWorkforce = new sileo.Model('hqzen', 'sidebar-workforce');

const APPLICANT = 1;

const state = {
    companyEmployments: {},
    companyUsers: {},
    workforces: [],

    // Rob (rob@bposeats.com): We are adding a fetch lock here because sometimes we really need to track if companyUsers
    // has already been fetched. This will be used mostly in `FetchUserMixin`. This may be less useful when we
    // start to store the users in IndexedDB or Local Storage, but I think this is needed for now.
    isFetchingCompanyUsers: false
};

const getters = {
    selectedWorkforceUsers: (state) => (workforce_id) => {
        let items = Object.values(
            state.companyUsers).filter(user => user.workforce_ids.includes(workforce_id));
        return items;
    },

    getCompanyUser: (state) => (user_id) => {
        if (!state.companyUsers[user_id]) {
            console.error('User not found. Maybe the user does not exist?');
            return null;
        }

        return state.companyUsers[user_id];
    },

    getCompanyEmployment: (state) => (employmentID) => {
        return state.companyEmployments[employmentID];
    },

    getCompanyEmploymentByUserId: (state) => (userId) => {
        return Object.values(state.companyEmployments).find(e => e.user_profile_id === userId);
    },

    getUsersByWorkforce: state => workforceID => {
        if (workforceID == -1) {// filter no workforce users
            return Object.values(state.companyUsers).filter(
                user => user.workforce_ids.length <= 0);
        }

        if (workforceID == 0) {// filter org executives
            return Object.values(state.companyUsers).filter(
                user => user.user_type != APPLICANT);
        }
        return Object.values(state.companyUsers).filter(
                user => user.workforce_ids.includes(workforceID));
    }
};

const mutations = {
    setCompanyUsers(state, users) {
        state.companyUsers = { ...state.companyUsers, ...users.reduce((companyUsers, user) => ({
            ...companyUsers,
            [user.pk]: user,
        }), {}) };
    },

    setWorkforces(state, workforces) {
        state.workforces = [ ...workforces ];
    },

    setCompanyEmployments(state, employments) {
        state.companyEmployments = employments.reduce((companyEmployments, employment) => ({
            ...companyEmployments,
            [employment.pk]: employment,
        }), {});
    },

    setCompanyEmployment(state, employment) {
        if (!employment.pk) return;
        const companyEmployments = { ...state.companyEmployments };
        companyEmployments[employment.pk] = employment;
        state.companyEmployments = companyEmployments;
    },

    addToCompanyEmployments(state, employment) {
        let companyEmployments = { ...state.companyEmployments };
        companyEmployments[employment.pk] = employment;
        state.companyEmployments = companyEmployments;
    },

    addOrUpdateCompanyUser(state, user) {
        if (!user.pk) return;
        let companyUsers = { ...state.companyUsers };
        companyUsers[user.pk] = user;
        state.companyUsers = companyUsers;
    },

    setIsFetchingCompanyUsers(state, isFetching) {
        state.isFetchingCompanyUsers = isFetching;
    }
};

const actions = {
    async fetchCompanyUsers(context, filters) {
        context.commit('setIsFetchingCompanyUsers', true);

        filters['top'] = 0;
        const sizePerRequest = 100;
        let companyUsersCount = 0;

        // Initial fetch to get count
        const data = await CompanyUser.objects.filter(filters);
        companyUsersCount = data.count;
        context.commit('setCompanyUsers', data.objects);
        filters['top'] += sizePerRequest;

        const companyUserApiCalls = [];
        let callBatch = '';

        while (filters['top'] < companyUsersCount) {
            try {
                // Use an IIFE to capture the current state of filters for each loop iteration
                ((currentFilters) => {
                    companyUserApiCalls.push(() => CompanyUser.objects.filter(currentFilters));
                })({ ...filters }); // Spread operator to clone filters object to avoid mutation issues

                filters['top'] += sizePerRequest;
            } catch (error) {
                callBatch = filters['top'];
                console.error(`Error in fetchCompanyUsers: ${error.statusText}`);
                break;
            }
            setTimeout(() => {
                console.log(`Fetching company users: ${filters['top']} out of ${companyUsersCount}`);
            }, 150);
        }
        const companyUsersData = await Promise.allSettled(companyUserApiCalls.map(apiCall => apiCall()));
        if (companyUsersData.some(call => call.status === 'rejected')) {
            console.error(`Failed to fetch all/some users in ${callBatch}`);
        } // Error checking for failed calls

        const failedCalls = companyUsersData
        .filter(result => result.status === 'rejected')
        .map((_, index) => companyUserApiCalls[index]);

        const companyUsers = companyUsersData
        .filter(call => call.status === 'fulfilled')
        .flatMap(companyUserData => companyUserData.value.objects);

        await context.commit('setCompanyUsers', companyUsers);
        context.commit('setIsFetchingCompanyUsers', false);

        if (failedCalls.length > 0) {
            console.log(`Retrying ${failedCalls.length} failed API calls in the background.`);

            setTimeout(async () => {
                let retryResults = await Promise.allSettled(failedCalls.map(apiCall => apiCall()));

                const retriedUsers = retryResults
                    .filter(result => result.status === 'fulfilled')
                    .flatMap(result => result.value.objects);

                if (retriedUsers.length > 0) {
                    await context.commit('setCompanyUsers', retriedUsers);
                    console.log('Successfully fetched and added retried company users.');
                }

                if (retryResults.some(result => result.status === 'rejected')) {
                    console.error('One or more retries for API calls still failed.');
                }
            }, 0);
        }
        return context.commit('setIsFetchingCompanyUsers', false);
    },

    fetchSpecificCompanyUser(context, pk) {
        return CompanyUser.objects.get(pk);
    },

    fetchSidebarWorkforces({ commit }, filters) {
        return SidebarWorkforce.objects.filter(filters)
            .then(data => {
                commit('setWorkforces', data);
                return data;
            }).catch(error => {
                console.error(error);
            });
    },

    fetchCompanyEmployments({ commit, state }, company) {
        return CompanyEmployment.objects.filter(company)
            .then(data => {
                commit('setCompanyEmployments', data);
                return state.companyEmployments;
            });
    },

    fetchCompanyEmploymentByID({ commit }, employmentID) {
        return CompanyEmployment.objects.get(employmentID)
            .then(data => {
                commit('addToCompanyEmployments', data);
                return data;
            });
    },

    fetchCompanyEmploymentByUserID(commit,filters) {
        return CompanyEmployment.objects.filter(filters);
    },

    async getUserProfile(context, pk) {
        return await CompanyUser.objects.get(pk);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
